import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import PaymentForm from './PaymentForm';
import Thank from './Thank';
import ReviewDoc from './ReviewDoc';
import ReviewCart from './ReviewCart';
import CustomerForm from './CustomerForm';
import { useTranslation } from 'react-i18next';
import { apiBase } from './api';
import { isItems, cartTotal, formatMoney } from './cart';


function Copyright() {
  const { t } = useTranslation();

  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {t("Copyright")}

      {' © '}
      <Link color="inherit" href="https://www.linet.org.il/">
        {t("LinetSoftwareSoultions")}
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({

  layout: {
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
      width: 600,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    width:'100%'
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
    width:'100%'

  },
}));

// A document link reviews a document; an item link reviews a basket and then asks who is
// paying, because nobody has told us yet. Same last two steps either way.
const DOC_STEPS = ['ReviewDoc', 'PaymentDetails', 'Confirmation'];
const ITEM_STEPS = ['ReviewCart', 'YourDetails', 'PaymentDetails', 'Confirmation'];


// currency_symbol lived here as a copy-pasted table; it is formatMoney in cart.js now, so
// the basket, the button and the receipt all format an amount the same way.

function getStepContent(stepName, data, onPaid, paidState, onAttempt) {

  switch (stepName) {
    case 'ReviewDoc':
      return <ReviewDoc data={data}/>;
    case 'ReviewCart':
      return <ReviewCart data={data}/>;
    case 'YourDetails':
      return <CustomerForm data={data} onAttempt={onAttempt} />;
    case 'PaymentDetails':
      return <PaymentForm data={data} onPaid={onPaid} />;
    case 'Confirmation':
      return <Thank state={paidState} />;
    default:
      throw new Error('Unknown step');
  }
}

export default function Checkout({data}) {
  const classes = useStyles();

  const { t } = useTranslation();

  const [activeStep, setActiveStep] = React.useState(0);

  /**
   * What the SERVER said about the payment, not what the gateway's iframe said. Set only by
   * PaymentForm once /paymentreq/state confirms a document (or once confirming times out,
   * with `pending`), and it is what the thank-you step is rendered from.
   */
  const [paidState, setPaidState] = React.useState(null);

  const items = isItems(data);
  const steps = items ? ITEM_STEPS : DOC_STEPS;

  /**
   * The minted attempt: its own hash and its own gateway session.
   *
   * The iframe step has to use THIS url, not `data.iframe_url` -- a link row has none, since
   * nothing is minted until the payer has given their details.
   */
  const [attempt, setAttempt] = React.useState(null);

  const handlePaid = React.useCallback((state) => {
    setPaidState(state);
    setActiveStep(steps.length - 1);
  }, [steps.length]);

  const handleAttempt = React.useCallback((minted) => {
    setAttempt(minted);
    setActiveStep(steps.indexOf('PaymentDetails'));
  }, [steps]);

  // The payment step talks to the attempt, not to the link.
  const payData = items && attempt
    ? { ...data, hash_id: attempt.hash_id, iframe_url: attempt.iframe_url }
    : data;

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const dType = (doc) => {
    return t("DocType"+doc.doctype)
  };

  return (
    <React.Fragment>
      <main className={classes.layout}>
        <Paper className={classes.paper}>
          <div style={{ textAlign:"center" }}>
            {/* A3: was a hardcoded app.linet.org.il, so a dev or staging page fetched its
                logo from production -- and a tenant on any other host got no logo at all. */}
            <img alt="logo" src={apiBase()+"/download/"+data.company_logo} width="150" />
          </div>
          <Typography component="h1" variant="h4" align="center">
            {/* An item link has a title the merchant wrote; a document has a type and a
                number. Neither reads sensibly in the other's shape. */}
            {items
              ? data.jsond_doc.title
              : <React.Fragment>{t("PayDocument")} {dType(data.jsond_doc)} #{data.jsond_doc.docnum}</React.Fragment>}
          </Typography>
          <Stepper activeStep={activeStep} className={classes.stepper}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{t(label)}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <React.Fragment>
            {activeStep === steps.length-1 ? (
              <Thank state={paidState} />
            ) : (
              <React.Fragment>
                {getStepContent(steps[activeStep], payData, handlePaid, paidState, handleAttempt)}
                <div className={classes.buttons}>
                  {activeStep !== 0 && steps[activeStep] !== 'PaymentDetails' && (
                    <Button
                    size="large"
                    onClick={handleBack}
                    className={classes.button}>
                      {t("Back")}
                    </Button>
                  )}
                  {/* Only the review step has a forward button. YourDetails submits its own
                      form, and PaymentDetails is driven by the gateway. */}
                  {activeStep === 0 && <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleNext}
                      className={classes.button}
                    >
                    {items
                      ? t("Pay") + " " + formatMoney(cartTotal(data.jsond_doc.lines), data.jsond_doc.currency_id)
                      : t("Pay") + " " + formatMoney(data.jsond_doc.total, data.jsond_doc.currency_id)}
                  </Button>}
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        </Paper>
        <Copyright />
      </main>
    </React.Fragment>
  );
}
